import type { PlanSlug, SubscriptionPlanPublic } from '@/api/types';
import type { UiKey } from '@/i18n/dictionary';
import { useT } from '@/i18n/LocaleProvider';
import { formatPrice, useSubscriptionPlans } from './useSubscriptionPlans';

/**
 * План, который окно оплаты выбирает само, когда вход не назвал план (`intent.plan`).
 * Цена до окна берётся у этого же плана: человек видит до окна ту цену, что окно
 * подсветит первой, и Continue без смены карточки оплачивает именно её.
 */
export const DEFAULT_PAYWALL_PLAN: PlanSlug = 'yearly';

/** Подпись периода — та же, что на карточке тарифа в окне (`PlanCards`). */
const PERIOD_KEY: Record<PlanSlug, UiKey> = {
  monthly: 'billing.perMonth',
  yearly: 'billing.perYear',
};

/**
 * Подпись цены — ровно та, что на карточке тарифа в окне оплаты (`PlanCards`:
 * `formatPrice` + период, «$72 / yr»). Без тарифа — null: вне окна цена не печатается
 * вовсе, ни «—», ни плейсхолдером.
 */
export function planPriceLabel(plan: SubscriptionPlanPublic | null, period: string): string | null {
  if (!plan) return null;
  const price = Number(plan.price);
  if (!Number.isFinite(price) || price <= 0) return null;
  return `${formatPrice(plan)} ${period}`;
}

/**
 * Цена до окна оплаты (счётчик квоты, баннер, Upgrade, строка последнего просмотра) —
 * цена плана, который окно выберет по умолчанию. Источник — тот же кэш
 * `GET /subscription-plans`, что у окна; пока тарифы не пришли — null.
 */
export function usePaywallPrice(): string | null {
  const t = useT();
  const prices = useSubscriptionPlans();
  return planPriceLabel(prices[DEFAULT_PAYWALL_PLAN], t(PERIOD_KEY[DEFAULT_PAYWALL_PLAN]));
}
